/**
 * Header - Presenter Component
 *
 * Pure presenter that renders the application header with navigation tabs,
 * modal trigger buttons, and logout. Receives all data and handlers from
 * HeaderContainer. Contains ZERO Zustand store access.
 *
 * Allowed hooks:
 * - useConfirmation (React context, not Zustand)
 *
 * @see docs/adr/0034-container-presenter-component-pattern.md
 */

import React from 'react';
import { NavLink } from 'react-router-dom';
import { Tooltip, Button } from 'antd';
import {
  BookOutlined,
  FormOutlined,
  FileTextOutlined,
  SoundOutlined,
  ThunderboltOutlined,
  MoonOutlined,
  FireOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { useConfirmation } from './providers/ConfirmationContext';
import styles from './Header.module.css';

// ---------------------------------------------------------------------------
// Props Interface
// ---------------------------------------------------------------------------

/** Modal handler functions bundled by HeaderContainer */
export interface HeaderModalHandlers {
  showLogModal: () => void;
  showScratchpadModal: () => void;
  showSessionModal: () => void;
  showSoundscapeModal: () => void;
  showDiceRollerModal: () => void;
  showWatchOrderModal: () => void;
}

/** Props for the Header presenter component */
export interface HeaderProps {
  /** Current authenticated user, or null if not logged in */
  user: { id: number; username: string; email: string; role: 'USER' | 'ADMIN' } | null;
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean;
  /** Whether any soundscapes are available (controls button disabled state) */
  soundscapesAvailable: boolean;
  /** Modal show handlers from UI store */
  modalHandlers: HeaderModalHandlers;
  /** Handler for rolling a random encounter (business logic lives in container) */
  onRollEncounter: () => void;
  /** Handler for logout (async) */
  onLogout: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Header: React.FC<HeaderProps> = ({
  user,
  isAuthenticated,
  soundscapesAvailable,
  modalHandlers,
  onRollEncounter,
  onLogout,
}) => {
  // useConfirmation is a React context hook (not Zustand), allowed in presenters
  const { confirm } = useConfirmation();

  const handleLogout = () => {
    confirm({
      message: 'Are you sure you want to log out?',
      onConfirm: onLogout,
      isAuthModal: true,
    });
  };

  return (
    <header className={styles.header}>
      <h1><NavLink to="/">DM Assistant</NavLink></h1>
      <nav className={styles.mainTabs}>
        <NavLink to="/" className={({ isActive }) => `${styles.tabLink} ${isActive ? styles.active : ''}`}>Dashboard</NavLink>
        <NavLink to="/data" className={({ isActive }) => `${styles.tabLink} ${isActive ? styles.active : ''}`}>Data</NavLink>
        <NavLink to="/world-building" className={({ isActive }) => `${styles.tabLink} ${isActive ? styles.active : ''}`}>World Building</NavLink>
        {user?.role === 'ADMIN' && (
          <NavLink to="/admin" className={({ isActive }) => `${styles.tabLink} ${isActive ? styles.active : ''}`}>Admin</NavLink>
        )}
        <NavLink to="/settings" className={({ isActive }) => `${styles.tabLink} ${isActive ? styles.active : ''}`}>Settings</NavLink>
      </nav>
      <div className={styles.icons}>
        <Tooltip title="Log">
          <button onClick={modalHandlers.showLogModal} aria-label="Open Campaign Log"><BookOutlined /></button>
        </Tooltip>
        <Tooltip title="Pad">
          <button onClick={modalHandlers.showScratchpadModal} aria-label="Open Scratchpad"><FormOutlined /></button>
        </Tooltip>
        <Tooltip title="Session">
          <button onClick={modalHandlers.showSessionModal} aria-label="Open Session Notes"><FileTextOutlined /></button>
        </Tooltip>
        <Tooltip title="Sounds">
          <button onClick={modalHandlers.showSoundscapeModal} aria-label="Open Soundscapes" disabled={!soundscapesAvailable}><SoundOutlined /></button>
        </Tooltip>
        <Tooltip title="Dice">
          <button onClick={modalHandlers.showDiceRollerModal} aria-label="Open Dice Roller"><ThunderboltOutlined /></button>
        </Tooltip>
        <Tooltip title="Watch">
          <button onClick={modalHandlers.showWatchOrderModal} aria-label="Open Camp Watch Order"><MoonOutlined /></button>
        </Tooltip>
        <Tooltip title="Roll Encounter">
          <button onClick={onRollEncounter} id="roll-encounter-btn" aria-label="Roll Encounter"><FireOutlined /></button>
        </Tooltip>
        {isAuthenticated && (
          <Tooltip title="Logout">
            <Button type="text" onClick={handleLogout} style={{ fontSize: '1.2rem', lineHeight: 1 }}><LogoutOutlined /></Button>
          </Tooltip>
        )}
      </div>
    </header>
  );
};

export default Header;
